// The tray of files waiting to be sent, as data. Lifted as is from the Andon
// app's `lib/staging.ts`.
//
// Pure, and separate from the hook that holds it, for the reason every other
// `lib/` module here is: the part worth testing is the state machine, and the
// part that is not worth testing is `useState`. Keeping them apart means these
// rules can be asserted without a DOM, which is what this client's test setup
// deliberately does not have.
//
// THE RULE ALL OF THIS SERVES: one bad file must not cost the other three.
//
// Uploading used to be sending — each pick became its own message the moment it
// finished. So a batch that failed halfway left some photos on the thread and
// some not, with no way to tell which from looking at it, and the only recovery
// was to pick the whole lot again. Here a file is staged, uploads on its own,
// and either gets an id or gets an error it wears until somebody removes it.
// Send takes the ids that exist.

export type AttachKind = 'photo' | 'audio' | 'video' | 'file';

export interface Staged {
  /** Stable across the upload. NOT the server's id, which arrives later. */
  key: string;
  name: string;
  kind: AttachKind;
  bytes: number;
  seconds: number | null;
  /** The server's attachment id, once it has one. Null while uploading. */
  id: number | null;
  /** Why this one will not go. Null while it still might. */
  error: string | null;
  /**
   * A local object URL, so a picture appears in the tray on the tap.
   *
   * From bytes already in the browser rather than from the server: waiting out
   * a round trip to show somebody the photo they just took is the kind of lag
   * that makes an app feel broken on a factory network.
   */
  preview: string | null;
}

/** Ten, matching the server's cap. Past that it is a folder, not a message. */
export const MAX_STAGED = 10;

export function stage(
  list: Staged[],
  entry: { key: string; name: string; kind: AttachKind; bytes: number; seconds: number | null; preview: string | null },
): Staged[] {
  return [...list, { ...entry, id: null, error: null }];
}

/**
 * Stage something the server ALREADY has.
 *
 * A picked GIF: the BFF fetched it from Giphy, sniffed it and stored it in
 * Odoo, so it arrives with an id and needs no upload of its own. It goes in the
 * tray as a finished row and travels with the message like any other file —
 * which is the point of routing it through here rather than giving GIFs their
 * own path through the composer.
 */
export function adopt(
  list: Staged[],
  entry: { key: string; id: number; name: string; kind: AttachKind; bytes: number; preview: string | null },
): Staged[] {
  return [...list, { ...entry, seconds: null, error: null }];
}

/** The upload landed. */
export function settled(list: Staged[], key: string, id: number): Staged[] {
  return list.map((s) => (s.key === key ? { ...s, id, error: null } : s));
}

/**
 * The upload failed, and the row STAYS.
 *
 * Dropping it would be the quiet version of the bug this design replaced:
 * somebody who watched a 214MB video upload needs to see which one failed and
 * why, and be able to send the other three without redoing them.
 */
export function failed(list: Staged[], key: string, error: string): Staged[] {
  return list.map((s) => (s.key === key ? { ...s, error } : s));
}

export function forget(list: Staged[], key: string): Staged[] {
  return list.filter((s) => s.key !== key);
}

/**
 * Every preview that needs revoking. `blob:` ONLY.
 *
 * A blob: URL pins its bytes until revoked. A same-origin one — an adopted
 * GIF, served from the attachment route — pins nothing, and calling
 * `revokeObjectURL` on it is a no-op that reads as though it did something.
 * Filtering here means the caller cannot get that wrong.
 */
export function previewsOf(list: Staged[]): string[] {
  return list.map((s) => s.preview).filter((u): u is string => u != null && u.startsWith('blob:'));
}

/** What goes to the server. Anything still uploading or failed is not in it. */
export function idsOf(list: Staged[]): number[] {
  return list.filter((s) => s.id != null).map((s) => s.id as number);
}

/** Something is still in flight. */
export function uploading(list: Staged[]): boolean {
  return list.some((s) => s.id == null && s.error == null);
}

/** Something will not go. */
export function anyFailed(list: Staged[]): boolean {
  return list.some((s) => s.error != null);
}

/** Still going up, and therefore not in the message yet. */
export function stillUploading(list: Staged[]): Staged[] {
  return list.filter((s) => s.id == null && s.error == null);
}

/**
 * Whether Send should do anything, for this draft and this tray.
 *
 * WORDS OR FILES — and NOT "never while one is still going up", which is what
 * this said and what made Send dead for minutes at a time.
 *
 * The old rule had a real argument behind it: a send that quietly dropped the
 * file that had not finished is indistinguishable, from the thread, from one
 * that never existed. What it did in practice was hold the urgent half of a
 * message hostage to the slow half. A 40MB clip of a jammed shear is minutes
 * on a factory uplink, and for all of those minutes the responder could not be
 * told anything at all — while the sentence "shear is jammed, need a hand" was
 * already typed and sitting in the box.
 *
 * So the file is no longer DROPPED, which removes the argument rather than
 * overruling it: the message posts with whatever has landed, and each late file
 * is attached to that same message as it arrives (see the composer's `send`,
 * and `andonApi.attachToMessage`). Nothing is silently lost, and the words go
 * at the speed words travel.
 *
 * A tray of nothing but failures is still not sendable, and that is the point
 * of the `stillUploading` term rather than a bare `list.length`: a failed row
 * is a row somebody has to retry or remove, not a message waiting to go.
 */
export function canSend(body: string, list: Staged[]): boolean {
  return body.trim().length > 0 || idsOf(list).length > 0 || stillUploading(list).length > 0;
}
